import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Truck, CreditCard, CheckCircle2, MapPinOff, PackageOpen } from 'lucide-react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Checkout() {
  const { cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({ name: '', street: '', city: '', state: 'NH', zip: '', phone: '' });
  const [preferences, setPreferences] = useState([]);
  const [deliveryPreference, setDeliveryPreference] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [specialNotes, setSpecialNotes] = useState('');
  const [route, setRoute] = useState(null);
  const [checkingRoute, setCheckingRoute] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/delivery/preferences`).then((r) => setPreferences(r.data.preferences)).catch(() => {});
  }, []);

  const lookupRoute = useCallback(async (city, zip) => {
    if (!city && !zip) { setRoute(null); return; }
    setCheckingRoute(true);
    try {
      const res = await axios.get(`${API}/delivery/lookup`, { params: { town: city, zip } });
      setRoute(res.data);
    } catch (error) {
      setRoute(null);
    } finally {
      setCheckingRoute(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => lookupRoute(address.city, address.zip), 500);
    return () => clearTimeout(timer);
  }, [address.city, address.zip, lookupRoute]);

  const deliveryFee = cart.subtotal >= 50 ? 0 : 4.99;
  const discount = cart.discount || 0;
  const preferenceFee = preferences.find((p) => p.label === deliveryPreference)?.fee || 0;
  const total = cart.subtotal - discount + deliveryFee + preferenceFee;
  const routeActive = route?.matched && route.status === 'Active';

  const handleJoinWaitlist = async () => {
    if (!address.city) {
      toast.error('Enter your city/town first');
      return;
    }
    try {
      setJoiningWaitlist(true);
      await axios.post(`${API}/delivery/waitlist`, {
        name: address.name || user?.name || '',
        email: user?.email || '',
        town: address.city,
        zip: address.zip,
        address: address.street,
      });
      setWaitlisted(true);
      toast.success("You're on the waitlist! We'll email you when delivery opens.");
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not join waitlist');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!address.name || !address.street || !address.city || !address.zip) {
      toast.error('Please fill in all address fields');
      return;
    }
    if (!routeActive) {
      toast.error("We don't deliver to your area yet. Join the waitlist below.");
      return;
    }
    if (!deliveryPreference) {
      toast.error('Please choose a delivery preference');
      return;
    }

    try {
      setLoading(true);
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        delivery_address: address,
        origin_url: originUrl,
        delivery_preference: deliveryPreference,
        delivery_instructions: deliveryInstructions,
        special_notes: specialNotes,
        phone: address.phone,
      }, {
        withCredentials: true
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const customBoxes = cart.custom_boxes || [];

  useEffect(() => {
    if (cart.items.length === 0 && customBoxes.length === 0) {
      navigate('/cart');
    }
  }, [cart.items.length, customBoxes.length, navigate]);

  if (cart.items.length === 0 && customBoxes.length === 0) {
    return null;
  }

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="checkout-page">
      <div className="container-custom py-12">
        <h1 className="font-playfair text-4xl font-bold text-dark mb-8" data-testid="checkout-title">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              {/* Delivery Address */}
              <div className="bg-white rounded-2xl p-8 border border-forest/10 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="w-6 h-6 text-forest" />
                  <h2 className="font-playfair text-2xl font-bold text-dark">Delivery Address</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name" className="font-manrope text-dark">Full Name</Label>
                    <Input
                      id="name"
                      value={address.name}
                      onChange={(e) => setAddress({ ...address, name: e.target.value })}
                      placeholder="John Doe"
                      className="mt-1 bg-cream border-forest/20"
                      data-testid="checkout-name"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="street" className="font-manrope text-dark">Street Address</Label>
                    <Input
                      id="street"
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      placeholder="123 Mountain View Rd"
                      className="mt-1 bg-cream border-forest/20"
                      data-testid="checkout-street"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="font-manrope text-dark">Town</Label>
                    <Input
                      id="city"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      placeholder="Berlin"
                      className="mt-1 bg-cream border-forest/20"
                      data-testid="checkout-city"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="state" className="font-manrope text-dark">State</Label>
                      <Input
                        id="state"
                        value={address.state}
                        onChange={(e) => setAddress({ ...address, state: e.target.value })}
                        placeholder="NH"
                        className="mt-1 bg-cream border-forest/20"
                        data-testid="checkout-state"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip" className="font-manrope text-dark">ZIP Code</Label>
                      <Input
                        id="zip"
                        value={address.zip}
                        onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                        placeholder="03570"
                        className="mt-1 bg-cream border-forest/20"
                        data-testid="checkout-zip"
                        required
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="phone" className="font-manrope text-dark">Phone (optional, for SMS delivery updates)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={address.phone}
                      onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                      placeholder="+1 603 555 0100"
                      className="mt-1 bg-cream border-forest/20"
                      data-testid="checkout-phone"
                    />
                  </div>
                </div>

                {/* Assigned delivery day/window or waitlist */}
                {!checkingRoute && address.city && (
                  routeActive ? (
                    <div className="mt-4 flex items-start gap-3 bg-forest/5 border border-forest/20 rounded-xl p-4" data-testid="delivery-assigned-banner">
                      <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                      <div className="font-manrope text-sm text-dark">
                        <p className="font-bold">Your FarmBox Delivery: {route.delivery_day}</p>
                        <p className="text-dark/60">Delivery Window: {route.delivery_window}</p>
                        <p className="text-dark/50 text-xs mt-1">You'll receive a more specific estimated delivery window before your delivery.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 bg-gold/10 border border-gold/30 rounded-xl p-4" data-testid="delivery-waitlist-banner">
                      <div className="flex items-start gap-3">
                        <MapPinOff className="w-5 h-5 text-gold-dark flex-shrink-0 mt-0.5" />
                        <p className="font-manrope text-sm text-dark/80">
                          FarmBox is expanding to your area. Join your area's waitlist and we'll let you know when delivery opens.
                        </p>
                      </div>
                      {waitlisted ? (
                        <p className="text-sm font-manrope text-forest font-medium mt-3">You're on the waitlist for {address.city}!</p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleJoinWaitlist}
                          disabled={joiningWaitlist}
                          className="btn-secondary py-2 px-5 text-sm mt-3"
                          data-testid="join-waitlist-btn"
                        >
                          {joiningWaitlist ? 'Joining...' : 'Join Waitlist'}
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>

              {/* Delivery preference */}
              <div className="bg-white rounded-2xl p-8 border border-forest/10 mb-6">
                <h2 className="font-playfair text-2xl font-bold text-dark mb-4">Delivery Preference</h2>
                <div className="space-y-2 mb-4">
                  {preferences.map((pref) => (
                    <label key={pref.label} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer font-manrope text-sm ${deliveryPreference === pref.label ? 'border-forest bg-forest/5' : 'border-forest/10'}`} data-testid={`delivery-pref-${pref.label.slice(0, 10).replace(/\W/g, '')}`}>
                      <input type="radio" name="delivery_preference" value={pref.label} checked={deliveryPreference === pref.label} onChange={() => setDeliveryPreference(pref.label)} className="accent-forest" />
                      <span className="flex-grow">{pref.label}</span>
                      {pref.fee > 0 && <span className="text-forest font-bold" data-testid={`delivery-pref-fee-${pref.label.slice(0, 10).replace(/\W/g, '')}`}>+${pref.fee.toFixed(2)}</span>}
                    </label>
                  ))}
                </div>
                <Label htmlFor="instructions" className="font-manrope text-dark">Delivery Instructions (optional)</Label>
                <Textarea
                  id="instructions"
                  value={deliveryInstructions}
                  onChange={(e) => setDeliveryInstructions(e.target.value)}
                  placeholder="Gate code, where to leave the box, etc."
                  className="mt-1 bg-cream border-forest/20"
                  data-testid="checkout-instructions"
                />
                <p className="text-xs text-dark/40 font-manrope mt-2">We can't promise an exact delivery time &mdash; you'll get an estimated ETA on delivery day.</p>
              </div>

              {/* Allergy / special handling */}
              <div className="bg-white rounded-2xl p-8 border border-forest/10 mb-6">
                <h2 className="font-playfair text-2xl font-bold text-dark mb-2">Allergies or Special Handling</h2>
                <p className="text-xs text-dark/50 font-manrope mb-3">Optional. This goes straight to FarmBox so we can handle your order with care.</p>
                <Textarea
                  value={specialNotes}
                  onChange={(e) => setSpecialNotes(e.target.value)}
                  placeholder="e.g. tree nut allergy, please keep separated from other produce"
                  className="bg-cream border-forest/20"
                  data-testid="checkout-special-notes"
                />
              </div>

              {/* Payment Info */}
              <div className="bg-white rounded-2xl p-8 border border-forest/10">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-6 h-6 text-forest" />
                  <h2 className="font-playfair text-2xl font-bold text-dark">Payment</h2>
                </div>

                <div className="bg-cream rounded-xl p-6 border border-forest/10">
                  <div className="flex items-center gap-3 mb-3">
                    <Lock className="w-5 h-5 text-forest" />
                    <span className="font-manrope font-medium text-dark">Secure Payment via Stripe</span>
                  </div>
                  <p className="font-manrope text-sm text-dark/60">
                    You will be redirected to Stripe's secure checkout to complete your payment. We accept all major credit cards.
                  </p>
                </div>
              </div>

              {/* Submit Button - Mobile */}
              <div className="lg:hidden mt-6">
                <button
                  type="submit"
                  disabled={loading || !routeActive}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                  data-testid="checkout-submit-mobile"
                >
                  {loading ? 'Processing...' : `Pay $${total.toFixed(2)}`}
                </button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 border border-forest/10 sticky top-28" data-testid="checkout-summary">
              <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Order Summary</h2>

              {/* Items */}
              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-3">
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-grow">
                      <p className="font-manrope font-medium text-dark text-sm">{item.product.name}</p>
                      <p className="text-xs text-dark/60">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-manrope font-medium text-dark">${item.item_total.toFixed(2)}</p>
                  </div>
                ))}
                {customBoxes.map((box) => (
                  <div key={box.box_id} className="flex gap-3">
                    <div className="w-16 h-16 bg-forest/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <PackageOpen className="w-6 h-6 text-forest" />
                    </div>
                    <div className="flex-grow">
                      <p className="font-manrope font-medium text-dark text-sm">Build Your Own {box.tier_name}</p>
                      <p className="text-xs text-dark/60">{box.item_count} items · {box.total_weight_lb} lb</p>
                    </div>
                    <p className="font-manrope font-medium text-dark">${box.box_total.toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-3 border-t border-forest/10 pt-4 mb-6">
                <div className="flex justify-between font-manrope">
                  <span className="text-dark/60">Subtotal</span>
                  <span className="text-dark">${cart.subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between font-manrope" data-testid="checkout-discount-row">
                    <span className="text-forest">{cart.discount_label || 'Discount'}</span>
                    <span className="text-forest">-${discount.toFixed(2)}</span>
                  </div>
                )}
                {cart.free_item && (
                  <div className="flex justify-between font-manrope text-sm" data-testid="checkout-free-item-row">
                    <span className="text-forest">+ 1 free {cart.free_item.name}</span>
                    <span className="text-forest">$0.00</span>
                  </div>
                )}
                <div className="flex justify-between font-manrope">
                  <span className="text-dark/60">Delivery</span>
                  <span className="text-dark">{deliveryFee === 0 ? 'Free' : `$${deliveryFee.toFixed(2)}`}</span>
                </div>
                {preferenceFee > 0 && (
                  <div className="flex justify-between font-manrope" data-testid="checkout-preference-fee-row">
                    <span className="text-dark/60">Thermal Porch Protection</span>
                    <span className="text-dark">+${preferenceFee.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-forest/10 pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-playfair text-xl font-bold text-dark">Total</span>
                  <span className="font-playfair text-xl font-bold text-dark" data-testid="checkout-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Submit Button - Desktop */}
              <button
                onClick={handleSubmit}
                disabled={loading || !routeActive}
                className="hidden lg:flex btn-primary w-full justify-center disabled:opacity-50"
                data-testid="checkout-submit"
              >
                {loading ? 'Processing...' : `Pay $${total.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
